import type { FC, PointerEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useSpring } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface MagneticProps {
    children: ReactNode
    className?: string
    strength?: number
}

const SPRING = { stiffness: 220, damping: 16, mass: 0.4 }

// Pulls its content gently towards the mouse cursor.
export const Magnetic: FC<MagneticProps> = ({ children, className, strength = 0.3 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const x = useSpring(0, SPRING)
    const y = useSpring(0, SPRING)
    const reduced = usePrefersReducedMotion()

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (reduced || event.pointerType !== 'mouse' || !ref.current) return

        const rect = ref.current.getBoundingClientRect()

        x.set((event.clientX - rect.left - rect.width / 2) * strength)
        y.set((event.clientY - rect.top - rect.height / 2) * strength)
    }

    const reset = () => {
        x.set(0)
        y.set(0)
    }

    return (
        <motion.div
            ref={ref}
            className={cn('inline-flex', className)}
            style={{ x, y }}
            onPointerMove={handlePointerMove}
            onPointerLeave={reset}
        >
            {children}
        </motion.div>
    )
}
